import React from "react";

import { useDropzone } from "react-dropzone";
import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";
import { CgFileRemove } from "react-icons/cg";

import Button, { Color } from "Components/Form/Button";
import IconUploadFile from "Components/Generic/Icons/IconUploadFile";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";

import { PDFViewerComponent } from "./PDFViewer";

const emptyPhoto = require("Assets/img/empty-documents.svg");

import {
  ContentTitle,
  Dropzone,
  DropzoneBoldInfoText,
  DropzoneButton,
  DropzoneInfoText,
  DropzoneText,
  EmptyDocumentWrapper,
  TextWrapper,
} from "./styles";
import { JobInventoryPDF } from "Models/job.inventory.pdf";

const TWO_MEGA_BYTES = 2 * 1024 * 1024;

interface UploadPDFFileProps {
  inventoryPdf?: JobInventoryPDF | null;
  onSubmitPdfFile?: (inventoryPdf: JobInventoryPDF | null) => void;
}

export const UploadPDFFile = ({ inventoryPdf, onSubmitPdfFile }: UploadPDFFileProps) => {
  const { t: translate } = useTranslation();

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: (acceptedFile) => {
      if (onSubmitPdfFile) {
        const reader = new FileReader();

        const inventoryPdfData: JobInventoryPDF = {
          filename: acceptedFile[0].name,
          fileType: acceptedFile[0].name.split(".").pop() || "",
          file: acceptedFile[0],
          url: "",
        };

        reader.addEventListener("load", () => {
          const uri = reader.result?.toString();
          if (uri) {
            inventoryPdfData.uri = uri;
            onSubmitPdfFile(inventoryPdfData);
          }
        });

        reader.readAsDataURL(acceptedFile[0]);
      }
    },
    onDropRejected: (rejections) => {
      const errors = rejections?.[0]?.errors;
      if (!errors?.length) return;

      const codes = new Set(errors.map((e) => e.code));
      if (codes.has("file-invalid-type")) {
        NotificationToast.error(translate("invalidFileType"));
        return;
      }
      if (codes.has("file-too-large")) {
        NotificationToast.error(translate("fileSizeIsTooLarge"));
        return;
      }
      NotificationToast.error(errors.map((e) => e.message).join(", "));
    },
    maxFiles: 1,
    maxSize: TWO_MEGA_BYTES,
    accept: [".pdf", "application/pdf"],
  });

  const returnEmptySection = () => (
    <EmptyDocumentWrapper>
      <img src={emptyPhoto} width={250} height={200} />
      <span>{translate("noDocumentsHaveBeenAddedYet")}</span>
    </EmptyDocumentWrapper>
  );

  return (
    <div>
      <ContentTitle>
        <span>{inventoryPdf ? translate("uploaded") : translate("uploadPDF")}</span>
        {inventoryPdf && (
          <Button
            startIcon={<CgFileRemove size={20} />}
            customColor={Color.ERROR_OUTLINED}
            onClick={() => onSubmitPdfFile?.(null)}
          >
            {translate("removeFile")}
          </Button>
        )}
      </ContentTitle>
      {inventoryPdf ? (
        <PDFViewerComponent
          inventoryPdf={inventoryPdf}
          onUpdateInventoryPdf={(record: Partial<JobInventoryPDF>) =>
            onSubmitPdfFile?.({ ...inventoryPdf, ...record } as JobInventoryPDF)
          }
        />
      ) : (
        <>
          <Dropzone {...getRootProps({ className: "dropzone" })}>
            <input {...getInputProps()} />
            <DropzoneButton>
              <IconUploadFile height={24} width={24} color={colors.brandBlue500} />
              <TextWrapper>
                <DropzoneText>{translate("dragOrClickToUploadYourFile")}</DropzoneText>
                <TextWrapper gap="0px">
                  <DropzoneBoldInfoText>
                    {translate("maxSize")}:{" "}
                    <DropzoneInfoText>{` ${translate("2Mb")}`}</DropzoneInfoText>
                  </DropzoneBoldInfoText>
                  <DropzoneBoldInfoText>
                    {translate("formats")}:{" "}
                    <DropzoneInfoText>{` ${translate("pdf")}`}</DropzoneInfoText>
                  </DropzoneBoldInfoText>
                </TextWrapper>
              </TextWrapper>
            </DropzoneButton>
          </Dropzone>
          {returnEmptySection()}
        </>
      )}
    </div>
  );
};
